import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { propType } from 'graphql-anywhere'
import { useMutation } from 'react-apollo'
import { useDispatch } from 'react-redux'
import classNames from 'classnames'
import styles from './devices.css'

import {
  DesktopTable,
  MobileTable,
  getTableColumns,
  NoData
} from './table-components'

import DEVICE_FRAGMENT from '../../../../../graphql/fragments/device.gql'
import DEVICES_QUERY from '../../../../../graphql/queries/devices.gql'
import DELETE_DEVICE_MUTATION from '../../../../../graphql/mutations/delete-device.gql'
import { displayErrorDialog } from '../../../../actions'
import { getGQLErrorMsg } from '../../../../lib/apollo'
import { useMutateAndLoading } from '../../../../hooks/use-mutate-and-loading'
import { segmentTrackDeleteDevice } from '../../../../segment/segment-track'

import ConfirmationModal from '../../../shared/confirmation-modal'

import ViewDownloadsModal from '../downloads/view-downloads-modal'

const handleTableHeadProps = () => ({
  className: classNames(styles.deviceTableHead, styles.header)
})

const handleTableRowGroupProps = () => ({
  className: classNames(styles.deviceTableRowGroup, styles.row)
})

const handleTableHeadThProps = (state, rowInfo, column) => {
  const isDeviceNameCol = column.id === 'name'
  const isSignoutDeviceCol = column.id === 'signout'

  return {
    className: classNames(
      styles.deviceTableHeadTh,
      { [styles.thFirst]: isDeviceNameCol },
      { [styles.thLast]: isSignoutDeviceCol }
    )
  }
}

const handleTableDataProps = (state, rowInfo, column) => {
  const isDeviceNameCol = column.id === 'name'
  const isSignoutDeviceCol = column.id === 'signout'

  return {
    className: classNames(
      styles.deviceTableData,
      { [styles.tdFirst]: isDeviceNameCol },
      { [styles.tdLast]: isSignoutDeviceCol }
    )
  }
}

const handleTableProps = (state, rowInfo, column, instance) => ({
  style: instance.props.data.length === 0 ? { display: 'none' } : {}
})

const MySavedDevicesTable = ({
  devices,
  isDesktopView,
  hasNoDevices,
  ...rest
}) => {
  const dispatch = useDispatch()
  const [deleteDeviceMutation] = useMutation(DELETE_DEVICE_MUTATION)
  const mutateAndLoading = useMutateAndLoading(deleteDeviceMutation)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [isViewDownloadsModalOpen, setIsViewDownloadsModalOpen] = useState(false)
  const [selectedDeviceUuid, setSelectedDeviceUuid] = useState('')

  if (hasNoDevices) {
    return <NoData />
  }

  const openConfirmationModal = (deviceUuid) => {
    setIsConfirmationModalOpen(true)
    setSelectedDeviceUuid(deviceUuid)
  }

  const closeConfirmationModal = () => {
    setIsConfirmationModalOpen(false)
    setSelectedDeviceUuid('')
  }

  const openViewDownloadsModal = (deviceUuid) => {
    setIsViewDownloadsModalOpen(true)
    setSelectedDeviceUuid(deviceUuid)
  }

  const closeViewDownloadsModal = () => {
    setIsViewDownloadsModalOpen(false)
    setSelectedDeviceUuid('')
  }

  const deleteDevice = deviceUuid => (
    mutateAndLoading('deviceDelete', {
      variables: {
        id: deviceUuid
      },
      update: (proxy, result) => {
        // Keep the saved devices query in sync after the device is removed.
        const data = proxy.readQuery({ query: DEVICES_QUERY })
        const cachedDevices = data?.devices || []
        const deviceDeletedSuccessfully = result?.data?.deleteDevice

        if (deviceDeletedSuccessfully) {
          const deletedDevice = cachedDevices.find(device => device.uuid === deviceUuid)
          const devicesAfterDelete = cachedDevices.filter(device => device.uuid !== deviceUuid)

          proxy.writeQuery({
            query: DEVICES_QUERY,
            data: { devices: devicesAfterDelete }
          })

          if (deletedDevice) {
            const {
              uuid, platform, os, name, model
            } = deletedDevice

            segmentTrackDeleteDevice({
              deviceId: uuid,
              platform,
              os,
              name,
              model
            })
          }
        }
      }
    }).catch((error) => {
      dispatch(displayErrorDialog({
        message: getGQLErrorMsg(error)
      }))
    })
  )

  const handleCancelClick = () => {
    closeConfirmationModal()
  }

  const handleConfirmClick = () => {
    deleteDevice(selectedDeviceUuid)
      .then(() => {
        closeConfirmationModal()
      })
  }

  const handleDeviceRemoval = (deviceUuid) => {
    openConfirmationModal(deviceUuid)
  }

  const handleViewDownloads = (deviceUuid) => {
    openViewDownloadsModal(deviceUuid)
  }

  const handleCloseModalClick = () => {
    closeViewDownloadsModal()
  }

  const tableColumns = getTableColumns({ handleDeviceRemoval, handleViewDownloads })

  return (
    <div>
      {
        isDesktopView ? (
          // Desktop view
          <DesktopTable
            devices={devices}
            className={styles.devicesAndDownloadsTable}
            tableColumns={tableColumns}
            handleTableProps={handleTableProps}
            handleTableDataProps={handleTableDataProps}
            handleTableHeadProps={handleTableHeadProps}
            handleTableRowGroupProps={handleTableRowGroupProps}
            handleTableHeadThProps={handleTableHeadThProps}
            {...rest}
          />
        ) : (
          // Mobile view
          <MobileTable
            devices={devices}
            tableColumns={tableColumns}
          />
        )
      }
      {
        isConfirmationModalOpen && (
          <ConfirmationModal
            header="Are you sure?"
            message={
              `You're about to permanently delete this device,
            are you sure this is what you'd like to do?`
            }
            confirmText="Yes, Delete this device"
            cancelText="Cancel"
            onCancelClick={handleCancelClick}
            onConfirmClick={handleConfirmClick}
          />
        )
      }
      {
        isViewDownloadsModalOpen && (
          <ViewDownloadsModal
            onModalClose={handleCloseModalClick}
            devices={devices}
            selectedDeviceUuid={selectedDeviceUuid}
          />
        )
      }
    </div>
  )
}

MySavedDevicesTable.propTypes = {
  devices: PropTypes.arrayOf(propType(DEVICE_FRAGMENT)).isRequired,
  isDesktopView: PropTypes.bool.isRequired,
  hasNoDevices: PropTypes.bool.isRequired
}

export default MySavedDevicesTable
